import { useEffect, useState } from "react";
import Card from "../../../components/ui/Card";
import Button from "../../../components/ui/Button";
import {
  getPublicContent,
  getAdminContentBySection,
} from "../../../api/content";
import ContentEditor from "./ContentEditor";
import { MdEdit, MdExpandMore } from "react-icons/md";
import "./ContentPage.css";

const LANGS = ["en", "ar", "ku"];
const PROTOCOL_SUFFIXES = [
  "id",
  "title",
  "machine",
  "protocol-overview",
  "what-is-included",
  "sessions",
  "treatment-time",
  "best-suited-for",
  "video-embed-url",
  "video-url-embedded",
  "videoembedurl",
  "videourlembedded",
];

const PAGES = [
  { key: "home", label: "Home" },
  { key: "about", label: "About" },
  { key: "doctors", label: "Doctors" },
  { key: "services", label: "Services" },
  { key: "devices", label: "Devices" },
  { key: "protocols", label: "Protocols" },
  { key: "contact", label: "Contact" },
];

const SECTIONS_BY_PAGE = {
  home: [
    "navigation",
    "hero",
    "clinicintroduction",
    "secondintro",
    "highlight",
    "treatments",
    "expertcare",
    "footer",
  ],
  about: ["hero", "commitment", "precision", "cards", "transformation"],
  doctors: ["hero", "doctor"],
  services: ["hero", "cards", "videosection"],
  devices: ["core"],
  protocols: ["core"],
  contact: ["hero", "form", "map"],
};

/* =========================
   MEDIA URL RESOLVER
========================= */
const MEDIA_BASE_URL =
  process.env.REACT_APP_MEDIA_BASE_URL ||
  process.env.REACT_APP_API_BASE_URL?.replace("/api", "") ||
  "";

const resolveMediaUrl = (path) => {
  if (!path) return "";
  if (path.startsWith("http")) return path;
  if (!path.startsWith("/")) return `${MEDIA_BASE_URL}/${path}`;
  return `${MEDIA_BASE_URL}${path}`;
};

const normalizeKey = (value) =>
  (value || "")
    .toString()
    .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
    .replace(/_/g, "-")
    .toLowerCase();

const getProtocolIdFromKey = (rawKey) => {
  const key = normalizeKey(rawKey);
  for (const suffix of PROTOCOL_SUFFIXES) {
    const token = `-${suffix}`;
    if (key.endsWith(token)) {
      return key.slice(0, -token.length);
    }
  }
  return null;
};

const toProtocolLabel = (id) =>
  (id || "")
    .split("-")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const ContentPage = () => {
  const [selectedPage, setSelectedPage] = useState("home");
  const [content, setContent] = useState({});
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);

  // dropdown state
  const [openSections, setOpenSections] = useState({});

  /* =========================
     LOAD PUBLIC CONTENT
  ========================= */
  const load = async () => {
    try {
      setLoading(true);

      const responses = await Promise.all(
        LANGS.map((l) =>
          getPublicContent(selectedPage, l)
        )
      );

      const merged = {};
      LANGS.forEach((l, i) => {
        merged[l] = responses[i].data;
      });

      setContent(merged);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    setOpenSections({});
  }, [selectedPage]);

  /* =========================
     OPEN EDITOR
  ========================= */
  const openEditor = async (section) => {
    const adminRes =
      await getAdminContentBySection(
        selectedPage,
        section
      );

    setEditing({
      page: selectedPage,
      section,
      items: adminRes.data || [],
    });
  };

  /* =========================
     HELPERS
  ========================= */
  const isMediaPath = (v) =>
    typeof v === "string" &&
    v.startsWith("/uploads");

  const isVideo = (v) =>
    v.endsWith(".mp4") || v.endsWith(".webm");

  const titleize = (str) =>
    str
      .replace(/_/g, " ")
      .replace(/\b\w/g, (l) =>
        l.toUpperCase()
      );

  const groupProtocolEntries = (sectionContent) => {
    const entries = Object.entries(sectionContent || {});
    const groups = new Map();
    const protocolIds = Array.from(
      new Set(
        entries
          .map(([key]) => getProtocolIdFromKey(key))
          .filter(Boolean)
      )
    );

    entries.forEach(([key, value]) => {
      const normalizedKey = normalizeKey(key);
      let groupName = "General";

      if (normalizedKey.startsWith("intro-")) {
        groupName = "Intro";
      } else {
        const matchedId = protocolIds.find((id) =>
          normalizedKey.startsWith(`${id}-`)
        );
        if (matchedId) {
          groupName = `Protocol: ${toProtocolLabel(matchedId)}`;
        }
      }

      if (!groups.has(groupName)) groups.set(groupName, []);
      groups.get(groupName).push([key, value]);
    });

    return Array.from(groups.entries()).map(([groupName, items]) => ({
      groupName,
      items,
    }));
  };

  const toggleSection = (section) => {
    setOpenSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  /* =========================
     RENDER VALUE
     media -> ONLY FOR EN
  ========================= */
  const renderValue = (key, value, lang) => {
    if (!value) return null;

    // object with text + image
    if (
      typeof value === "object" &&
      value.text &&
      value.image
    ) {
      return (
        <p key={key} className="text-sm">
          <b>{titleize(key)}:</b>{" "}
          {value.text}
        </p>
      );
    }

    // media path
    if (isMediaPath(value)) {
      const src = resolveMediaUrl(value);

      if (isVideo(value)) {
        return (
          <div key={key} className="space-y-2">
            <p className="text-sm">
              <b>{titleize(key)}:</b>
            </p>
            <video
              src={src}
              controls
              preload="metadata"
              playsInline
              className="aspect-video w-full rounded-md bg-black"
            >
              Your browser does not support the video tag.
            </video>
          </div>
        );
      }

      return (
        <img
          key={key}
          src={src}
          alt=""
          className="w-full rounded-md"
        />
      );
    }

    // pure text
    if (typeof value === "string") {
      return (
        <p key={key} className="text-sm">
          <b>{titleize(key)}:</b>{" "}
          {value}
        </p>
      );
    }

    return null;
  };

  /* =========================
     UI
  ========================= */
  return (
    <>
      <div className="space-y-6">
        <Card title="Website Pages">
          <div className="flex gap-2 flex-wrap">
            {PAGES.map((p) => (
              <button
                key={p.key}
                onClick={() =>
                  setSelectedPage(p.key)
                }
                className={`px-4 py-2 rounded-lg ${
                  selectedPage === p.key
                    ? "bg-[#95785A] text-white"
                    : "bg-gray-100"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </Card>

        {SECTIONS_BY_PAGE[selectedPage]?.map(
          (section) => {
            const isOpen =
              openSections[section];

            return (
              <Card key={section}>
                {/* HEADER */}
                <div
                  className="flex items-center justify-between cursor-pointer select-none"
                  onClick={() =>
                    toggleSection(section)
                  }
                >
                  <h3 className="text-lg font-semibold">
                    {titleize(section)}
                  </h3>

                  <MdExpandMore
                    size={26}
                    className={`transition-transform ${
                      isOpen
                        ? "rotate-180"
                        : ""
                    }`}
                  />
                </div>

                {/* BODY */}
                {isOpen && !loading && (
                  <div className="pt-4 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {LANGS.map((lang) => (
                        <div
                          key={lang}
                          className="border rounded-lg p-3 space-y-2"
                        >
                          <div className="lang-label">
                            {lang}
                          </div>

                          {content[lang]?.[
                            section
                          ] ? (
                            selectedPage === "protocols" &&
                            section === "core" ? (
                              <div className="space-y-3">
                                {groupProtocolEntries(content[lang][section]).map(
                                  (group) => (
                                    <article
                                      key={`${lang}-${group.groupName}`}
                                      className="border rounded-lg overflow-hidden"
                                    >
                                      <div className="px-3 py-2 bg-gray-50 border-b text-xs font-semibold text-gray-700">
                                        {group.groupName}
                                      </div>
                                      <div className="p-3 space-y-2">
                                        {group.items.map(([k, v]) => (
                                          <div
                                            key={`${lang}-${group.groupName}-${k}`}
                                            className="border rounded-md p-2 bg-white"
                                          >
                                            {renderValue(k, v, lang)}
                                          </div>
                                        ))}
                                      </div>
                                    </article>
                                  )
                                )}
                              </div>
                            ) : (
                              Object.entries(
                                content[lang][
                                  section
                                ]
                              ).map(([k, v]) =>
                                renderValue(
                                  k,
                                  v,
                                  lang
                                )
                              )
                            )
                          ) : (
                            "No content"
                          )}
                        </div>
                      ))}
                    </div>

                    <Button
                      variant="outline"
                      onClick={() =>
                        openEditor(section)
                      }
                    >
                      <MdEdit className="mr-2" />
                      Edit Section
                    </Button>
                  </div>
                )}
              </Card>
            );
          }
        )}
      </div>

      {/* EDIT MODAL */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center">
          <div className="bg-white w-full max-w-2xl max-h-[85vh] overflow-y-auto p-5 rounded-xl shadow-xl">
            <h2 className="text-xl font-semibold mb-4">
              Edit {editing.section}
            </h2>

            <ContentEditor
              items={editing.items}
              page={editing.page}
              section={editing.section}
              onSaved={() => {
                setEditing(null);
                load();
              }}
            />

            <div className="flex justify-end mt-6">
              <Button
                onClick={() =>
                  setEditing(null)
                }
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ContentPage;
